import React, { useState, useMemo } from 'react';
import { FeedsDataItem, ErrorRecord } from '../feeds_list_types';
import { TblCol, TblSection, TblSort, TblSortDir } from '../../../../core/table/table.types';
import TableHead from '../../../../core/table/TableHead';
import TableBody from '../../../../core/table/TableBody';
import Button from '../../../../core/components/Button';
import TableList from '../../../../core/table/TableList';

interface ErrorRecordsPanelProps {
    feedItem: FeedsDataItem;
}

const ErrorRecordsPanel = ({ feedItem }: ErrorRecordsPanelProps): JSX.Element | null => {
    const [showFullRecords, setShowFullRecords] = useState(false);
    const [errorTableFilters, setErrorTableFilters] = useState({});
    const [errorTableSort, setErrorTableSort] = useState<TblSort>({ col: 'LineNum', dir: 'ASC' });

    // Error records table configuration
    const errorTableCols: TblCol[] = useMemo(
        () => [
            {
                key: 'LineNum',
                type: 'NUMBER',
                title: 'Line #',
                width: '10%',
                hasSort: true,
                hasFilter: true,
                fltOptions: [],
            },
            {
                key: 'RawData',
                type: 'TEXT',
                title: 'Raw Data',
                width: '60%',
                hasSort: true,
                hasFilter: true,
                fltOptions: [],
            },
            {
                key: 'Errors',
                type: 'TEXT',
                title: 'Errors',
                width: '30%',
                hasSort: false,
                hasFilter: true,
                fltOptions: [],
                renderer: ({ item }) => (
                    <div>
                        {Array.isArray(item.Errors)
                            ? item.Errors.map((error: string, errIndex: number) => (
                                  <div
                                      key={errIndex}
                                      style={{ whiteSpace: 'pre-wrap' }}
                                  >
                                      {error}
                                  </div>
                              ))
                            : item.Errors}
                    </div>
                ),
            },
        ],
        [],
    );

    const errorTableSections: TblSection[] = useMemo(
        () => [
            {
                title: 'Record Info',
                span: 2,
            },
            {
                title: 'Errors',
                span: 1,
            },
        ],
        [],
    );

    // Handle Records data
    const errorRecords: ErrorRecord[] = useMemo(
        () => (Array.isArray(feedItem.Records) ? feedItem.Records : []),
        [feedItem.Records],
    );

    const hasErrorRecords = errorRecords.length > 0;

    // Filter and sort error records based on React table filters and sorting
    const filteredAndSortedRecords = useMemo(() => {
        let filtered = [...errorRecords];

        // Apply filters from the React table
        Object.entries(errorTableFilters).forEach(([key, value]) => {
            if (value && typeof value === 'string' && value.trim()) {
                filtered = filtered.filter(record => {
                    const recordValue = record[key as keyof ErrorRecord];
                    if (Array.isArray(recordValue)) {
                        // For errors array, search within each error string
                        return recordValue.some(err => err.toLowerCase().includes(value.toLowerCase()));
                    }
                    return String(recordValue).toLowerCase().includes(value.toLowerCase());
                });
            }
        });

        // Apply sorting
        if (errorTableSort.col && errorTableSort.dir !== '') {
            filtered.sort((a, b) => {
                const aVal = a[errorTableSort.col as keyof ErrorRecord];
                const bVal = b[errorTableSort.col as keyof ErrorRecord];

                let comparison = 0;
                if (typeof aVal === 'number' && typeof bVal === 'number') {
                    comparison = aVal - bVal;
                } else {
                    comparison = String(aVal).localeCompare(String(bVal));
                }

                return errorTableSort.dir === 'DSC' ? -comparison : comparison;
            });
        }

        return filtered;
    }, [errorRecords, errorTableFilters, errorTableSort]);

    const displayedRecords = showFullRecords ? filteredAndSortedRecords : filteredAndSortedRecords.slice(0, 50);

    // Table filter and sort handlers
    const handleErrorTableFilter = (key: string, val: any): void => {
        setErrorTableFilters(prev => ({ ...prev, [key]: val }));
    };

    const handleErrorTableSort = (col: string, dir?: TblSortDir): void => {
        setErrorTableSort({ col, dir: dir || 'ASC' });
    };

    // Don't render if no error records
    if (!hasErrorRecords) {
        return null;
    }

    return (
        <section className="error-records-section panel-spacing">
            <h3>Error Records</h3>
            <div className="error-records-header">
                <span>
                    Viewing Top {displayedRecords.length} of {errorRecords.length} Error Records
                </span>
            </div>

            <TableList>
                <table>
                    <TableHead
                        cols={errorTableCols}
                        sections={errorTableSections}
                        sort={errorTableSort}
                        filters={errorTableFilters}
                        setFilter={handleErrorTableFilter}
                        setSort={handleErrorTableSort}
                    />
                    <TableBody
                        cols={errorTableCols}
                        rows={displayedRecords}
                    />
                </table>
            </TableList>

            {filteredAndSortedRecords.length > displayedRecords.length && (
                <div className="show-more-records">
                    <Button
                        onClick={() => setShowFullRecords(!showFullRecords)}
                        cssName="show-more-button"
                    >
                        {showFullRecords
                            ? 'Show Top 50 Records'
                            : `Show All ${filteredAndSortedRecords.length} Records`}
                    </Button>
                </div>
            )}

            {filteredAndSortedRecords.length === 0 && errorRecords.length > 0 && (
                <div className="no-filtered-records">
                    <p>No records to display. Note: {errorRecords.length} rows are hidden due to column filters.</p>
                </div>
            )}
        </section>
    );
};

export default React.memo(ErrorRecordsPanel);
